import React, { useEffect, useState, useCallback } from "react";
import { api } from "../api/client";
import {
  PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { RefreshCw, Calendar, Clock, Bell, UserPlus, AlertTriangle } from "lucide-react";
import DateRangePicker from "../components/DateRangePicker";
import { stageColor } from "../constants/stageColors";
import { useStagesV2 } from "../lib/stagesV2";

/* ── Design tokens ──────────────────────────────────────────────────────── */
const STAT_CONFIGS = [
  { id: "ready_to_schedule", label: "Ready to Schedule", icon: UserPlus, textCls: "text-emerald-600" },
  { id: "active_pipeline",   label: "Active Pipeline",   icon: Bell,     textCls: "text-blue-600"    },
  { id: "needs_reschedule",  label: "Needs Reschedule",  icon: Clock,    textCls: "text-orange-600"  },
  { id: "scheduled",         label: "Scheduled",         icon: Calendar, textCls: "text-purple-600"  },
];

const NEW_REFERRAL_COLOR = "#94a3b8"; // neutral slate

const STAGE_AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-purple-100 text-purple-700",
  "bg-orange-100 text-orange-700",
  "bg-rose-100 text-rose-700",
  "bg-teal-100 text-teal-700",
  "bg-amber-100 text-amber-700",
  "bg-indigo-100 text-indigo-700",
];

function fmtAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function initials(str) {
  if (!str) return "?";
  return str.split(/[\s_-]/).map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

function SectionError({ message }) {
  return (
    <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 my-2" data-testid="section-error">
      <AlertTriangle size={14} className="flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}

/* ── Stat Card ──────────────────────────────────────────────────────────── */
function StatCard({ config, value, loading, error }) {
  const Icon = config.icon;
  return (
    <div
      data-testid={`stat-card-${config.id}`}
      className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
    >
      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{config.label}</span>
      {loading ? (
        <div className="h-9 w-16 bg-slate-100 rounded-lg animate-pulse mt-1" />
      ) : error ? (
        <span className="block text-sm font-semibold text-red-600 mt-2">Failed to load</span>
      ) : (
        <span className="block text-4xl font-bold text-slate-900 leading-none">{value ?? "—"}</span>
      )}
      <span className={`inline-flex items-center gap-1 mt-2 text-xs font-semibold ${config.textCls}`}>
        <Icon size={13} />
        Live
      </span>
    </div>
  );
}

/* ── Journey Line Chart ─────────────────────────────────────────────────── */
function JourneyTrendChart({ data, loading, error }) {
  if (loading) return <div className="h-72 bg-slate-50 rounded-xl animate-pulse" />;
  if (error) return <SectionError message={error} />;
  // Show the arrivals line only when BQ actually returned data. If every
  // bucket has new_referrals == null the BQ trend query failed (or we're
  // in mock mode) — render only the stage-change series, no empty second
  // line, no crash.
  const showNewReferrals =
    Array.isArray(data) && data.some((d) => d && d.new_referrals != null);
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data || []} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
        <CartesianGrid strokeDasharray="4 4" stroke="#f1f5f9" vertical={false} />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          dy={8}
          tickFormatter={(d) => (d ? d.slice(5) : "")}
        />
        <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} dx={-4} allowDecimals={false} />
        <Tooltip
          contentStyle={{ border: "1px solid #e2e8f0", borderRadius: "12px", fontSize: "12px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}
          formatter={(value, name) => [value ?? "—", name]}
        />
        <Line
          type="monotone"
          dataKey="count"
          name="Stage Changes"
          stroke="#3b82f6"
          strokeWidth={2.5}
          dot={{ r: 3, fill: "#3b82f6", strokeWidth: 0 }}
          activeDot={{ r: 5 }}
        />
        {showNewReferrals && (
          <Line
            type="monotone"
            dataKey="new_referrals"
            name="New Referrals In"
            stroke="#10b981"
            strokeWidth={2.5}
            dot={{ r: 3, fill: "#10b981", strokeWidth: 0 }}
            activeDot={{ r: 5 }}
            connectNulls
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

/* ── Pipeline Donut Chart ───────────────────────────────────────────────── */
function PipelineDonutChart({ pipeline, loading, error }) {
  if (loading) return <div className="flex-1 bg-slate-50 rounded-xl animate-pulse" />;
  if (error) return <SectionError message={error} />;

  const slices = (pipeline?.stages || []).map((s) => ({
    key: `s${s.stage_id}`,
    name: s.stage_name,
    count: s.count,
    color: stageColor(s.stage_id),
  }));
  if (pipeline?.new_referral != null && pipeline.new_referral > 0) {
    slices.push({ key: "new_referral", name: "New Referral", count: pipeline.new_referral, color: NEW_REFERRAL_COLOR });
  }
  const total = slices.reduce((s, d) => s + d.count, 0);
  if (!total) return <p className="text-sm text-slate-400 py-8 text-center">No active patients</p>;

  return (
    <div className="flex flex-col items-center flex-1" data-testid="pipeline-donut">
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie
            data={slices}
            dataKey="count"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={62}
            outerRadius={90}
            paddingAngle={3}
            cornerRadius={4}
          >
            {slices.map((entry) => (
              <Cell key={entry.key} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{ border: "1px solid #e2e8f0", borderRadius: "12px", fontSize: "12px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}
            formatter={(value, name) => [
              `${value} (${total ? Math.round((value / total) * 100) : 0}%)`,
              name,
            ]}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="w-full mt-2 space-y-1.5 max-h-40 overflow-y-auto">
        {slices.map((entry) => {
          const pct = total ? Math.round((entry.count / total) * 100) : 0;
          return (
            <div key={entry.key} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2 min-w-0">
                <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                <span className="text-slate-600 truncate">{entry.name}</span>
              </span>
              <span className="text-slate-500 font-semibold flex-shrink-0 ml-2">{entry.count} · {pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Needs Attention List ───────────────────────────────────────────────── */
function NeedsAttentionList({ patients, loading, error }) {
  if (loading) return (
    <div className="space-y-3 flex-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={`skeleton-${i}`} className="h-14 bg-slate-50 rounded-xl animate-pulse" />
      ))}
    </div>
  );
  if (error) return <SectionError message={error} />;
  if (!patients?.length) return <p className="text-sm text-slate-400 py-8 text-center">No active patients</p>;
  return (
    <div className="flex-1 overflow-y-auto space-y-1 -mx-1 px-1" data-testid="needs-attention-list">
      {patients.map((p, i) => {
        const avatarCls = STAGE_AVATAR_COLORS[i % STAGE_AVATAR_COLORS.length];
        const isUrgent = p.days_in_stage >= 3;
        return (
          <div key={p.id} className="flex items-center justify-between py-2.5 px-2 rounded-lg hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarCls}`}>
                {initials(p.patient_name)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{p.patient_name}</p>
                <p className="text-xs text-slate-400 truncate">{p.stage_name || "—"}</p>
              </div>
            </div>
            <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ml-3 ${isUrgent ? "bg-orange-50 text-orange-700" : "bg-slate-100 text-slate-500"}`}>
              {p.days_in_stage}d
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Recent Activity Table ──────────────────────────────────────────────── */
function RecentActivityTable({ data, loading, error }) {
  if (loading) return (
    <div className="space-y-3 flex-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={`skeleton-activity-${i}`} className="h-12 bg-slate-50 rounded-xl animate-pulse" />
      ))}
    </div>
  );
  if (error) return <SectionError message={error} />;
  if (!data?.length) return <p className="text-sm text-slate-400 py-8 text-center">No recent activity</p>;
  return (
    <div className="flex-1 overflow-y-auto -mx-1 px-1" data-testid="recent-activity-list">
      <div className="grid grid-cols-3 gap-2 px-2 mb-2">
        {["Patient", "Action", "When"].map(h => (
          <span key={h} className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</span>
        ))}
      </div>
      <div className="space-y-1">
        {data.map((row, i) => {
          const avatarCls = STAGE_AVATAR_COLORS[(row.patient_name || "?").charCodeAt(0) % STAGE_AVATAR_COLORS.length];
          return (
            <div key={`${row.entered_at}-${i}`} className="grid grid-cols-3 gap-2 items-center py-2 px-2 rounded-lg hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0">
              <div className="flex items-center gap-2 min-w-0">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarCls}`}>
                  {initials(row.patient_name)}
                </div>
                <span className="text-xs text-slate-600 font-medium truncate">{row.patient_name || "—"}</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-700 truncate">→ {row.stage_name || "—"}</p>
                <p className="text-xs text-slate-400 truncate">{row.username || "—"}</p>
              </div>
              <span className="text-xs text-slate-400">{fmtAgo(row.entered_at)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Legend pill for line chart ─────────────────────────────────────────── */
function LegendPill({ color, label }) {
  return (
    <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
      <span className="inline-block h-2 w-4 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

/* ── Main Dashboard ─────────────────────────────────────────────────────── */
export default function SalesDashboard() {
  // CRM statuses v2: stage colours from GET /crm/stages when STATUS_NAMES_V2 is on.
  useStagesV2();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  // Date range filter state
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/dashboard/sales-data", {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(res.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load dashboard data");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

  const activeCount = (data?.pipeline?.stages || [])
    .filter(s => s.stage_name !== "Scheduled Initial Visit")
    .reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="min-h-screen bg-slate-50 overflow-y-auto pb-8">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-5 flex items-center justify-between sticky top-0 z-10">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Referral Intake Dashboard</h1>
          {lastUpdated && (
            <p className="text-xs text-slate-400 mt-0.5">Last updated {fmtTime(lastUpdated)}</p>
          )}
        </div>
        <div className="flex items-center gap-4">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartChange={setStartDate}
            onEndChange={setEndDate}
          />
          <button
            data-testid="dashboard-refresh-btn"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      <div className="px-8 py-6 space-y-6 max-w-screen-2xl mx-auto">

        {error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm" data-testid="dashboard-error-banner">
            <AlertTriangle size={16} className="flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={load} className="font-semibold underline hover:no-underline">Retry</button>
          </div>
        )}

        {/* Row 1 — Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STAT_CONFIGS.map(cfg => (
            <StatCard
              key={cfg.id}
              config={cfg}
              value={data?.stats?.[cfg.id]}
              loading={loading}
              error={error}
            />
          ))}
        </div>

        {/* Row 2 — Line Chart + Donut */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Line Chart — 7 cols */}
          <div className="lg:col-span-7 bg-white border border-slate-200 rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-base font-bold text-slate-900">Daily Journey Activity</h2>
                <p className="text-xs text-slate-400 mt-0.5">Team-wide stage changes per day (Central time)</p>
              </div>
              <div className="flex items-center gap-3">
                <LegendPill color="#3b82f6" label="Stage Changes" />
                {Array.isArray(data?.journey_trend) &&
                  data.journey_trend.some((d) => d && d.new_referrals != null) && (
                    <LegendPill color="#10b981" label="New Referrals In" />
                  )}
              </div>
            </div>
            <JourneyTrendChart data={data?.journey_trend} loading={loading} error={error} />
          </div>

          {/* Donut — 5 cols */}
          <div className="lg:col-span-5 bg-white border border-slate-200 rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow flex flex-col">
            <div className="mb-4">
              <h2 className="text-base font-bold text-slate-900">Pipeline Breakdown</h2>
              <p className="text-xs text-slate-400 mt-0.5">Active patients by CRM stage, plus new referrals in intake</p>
            </div>
            <PipelineDonutChart pipeline={data?.pipeline} loading={loading} error={error} />
          </div>
        </div>

        {/* Row 3 — Needs Attention + Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Needs Attention */}
          <div className="lg:col-span-6 bg-white border border-slate-200 rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow flex flex-col" style={{ minHeight: 400 }}>
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div>
                <h2 className="text-base font-bold text-slate-900">Needs Attention</h2>
                <p className="text-xs text-slate-400 mt-0.5">Active patients sorted by longest time in stage</p>
              </div>
              <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full">
                {activeCount} active
              </span>
            </div>
            <NeedsAttentionList patients={data?.needs_attention} loading={loading} error={error} />
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-6 bg-white border border-slate-200 rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow flex flex-col" style={{ minHeight: 400 }}>
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div>
                <h2 className="text-base font-bold text-slate-900">Recent Activity</h2>
                <p className="text-xs text-slate-400 mt-0.5">Latest stage changes across all patients</p>
              </div>
              <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
                {(data?.recent_activity || []).length} updates
              </span>
            </div>
            <RecentActivityTable data={data?.recent_activity} loading={loading} error={error} />
          </div>
        </div>

      </div>
    </div>
  );
}
